/**
 * A performance put back on a grid, so that it can be written down like any other file.
 *
 * A file exported from a notation program or a sequencer keeps its notes on its own beat: a
 * quarter note is `division` ticks, and snapping to a 16th grid is all transcription needs.
 * A file recorded off a keyboard does not. Its ticks count clock time at whatever tempo the
 * recorder assumed, so its beats fall anywhere, and snapping it writes noise. Such a file is
 * re-timed here, before transcription sees it: its beats are found ({@link beatsOf}), the
 * bars are followed through them ({@link metreOf}), a beat that was only time the player took
 * is left out, and every note is moved to where it falls between the beats around it, so a
 * beat becomes exactly one written beat whatever the player's tempo did. The
 * file that comes back states the metre, a tempo, and a key when it had none
 * ({@link keyOf}), and its first bar line falls on the first downbeat.
 *
 * Which files are performances is itself read, never assumed: the onsets are measured
 * against the file's own grid and against the grid the beats give, and the file is re-timed
 * only when the found grid fits them clearly better. A file that is written but slightly
 * humanised stays on its own grid.
 */

import { PPQ } from "../document/duration";
import { beatsOf } from "./beats";
import { keyOf } from "./key";
import { metreOf } from "./metre";
import { attacksOf, secondsOf, timedNotes } from "./onsets";
import type { MidiFile, MidiTrack } from "./read";

/** Onsets needed before a file is judged a performance at all. */
const ENOUGH = 16;
/** How much better the found grid must fit before a file is re-timed. */
const CLEARLY = 0.8;
/**
 * How far off its own grid a file must sit before it can be a recording: three quarters of
 * the median distance chance gives, which is a 24th of a beat on the grid below. A written
 * file played back with its notes nudged sits nearer than this, a recording on a clock that
 * knows nothing of its beat sits at chance.
 */
const ADRIFT = 0.75 / 24;
/** The finest written step: a 48th of a beat covers 32nds, 16th triplets and 32nd triplets. */
const STEPS = 48;
/** The resolution below which an exact tick says nothing, since every gap lands on a step. */
const FINE = 192;
/** The share of gaps on a written step, to the tick, from which a file is taken as written. */
const WRITTEN = 0.5;

/** What re-timing did, for the caller to say. */
export interface Retimed {
  readonly file: MidiFile;
  readonly warnings: readonly string[];
}

/** The median distance of `positions`, in beats, from the nearest 16th or 8th-note triplet. */
function offGrid(positions: readonly number[]): number {
  const grid = [0, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4, 1];
  const distances = positions
    .map((position) => position - Math.floor(position))
    .map((fraction) => Math.min(...grid.map((point) => Math.abs(fraction - point))))
    .sort((first, second) => first - second);
  return distances[Math.floor(distances.length / 2)] ?? 0;
}

/**
 * The share of gaps between attacks that are a whole number of written steps, to the tick.
 * A notation program writes every gap so, whatever the file's offset or odd-length bars; a
 * recording lands on a step only by chance. Keys struck within a 64th are one attack.
 */
function exactShare(onsets: readonly number[], division: number): number {
  const attacks: number[] = [];
  for (const tick of onsets) {
    const last = attacks[attacks.length - 1];
    if (last === undefined || tick - last > division / 16) attacks.push(tick);
  }
  const step = division / STEPS;
  const gaps = attacks.slice(1).map((tick, index) => tick - (attacks[index] as number));
  const exact = gaps.filter((gap) => Math.abs(gap / step - Math.round(gap / step)) * step <= 0.5);
  return exact.length / Math.max(1, gaps.length);
}

/** A clock mapping seconds to beats, through the beat times and straight on past either end. */
function beatClock(beats: readonly number[]): (seconds: number) => number {
  const first = beats[0] as number;
  const firstGap = (beats[1] as number) - first;
  const last = beats[beats.length - 1] as number;
  const lastGap = last - (beats[beats.length - 2] as number);
  return (seconds) => {
    if (seconds <= first) return (seconds - first) / firstGap;
    if (seconds >= last) return beats.length - 1 + (seconds - last) / lastGap;
    let low = 0;
    let high = beats.length - 1;
    while (high - low > 1) {
      const middle = (low + high) >> 1;
      if ((beats[middle] as number) <= seconds) low = middle;
      else high = middle;
    }
    const start = beats[low] as number;
    return low + (seconds - start) / ((beats[high] as number) - start);
  };
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((first, second) => first - second);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/**
 * The file itself when it is written on its own grid, or the performance re-timed onto the
 * beats found in it.
 */
export function unperformed(file: MidiFile): Retimed {
  const division = file.division > 0 ? file.division : 480;
  const onsets = [...new Set(file.tracks.flatMap((track) => track.notes.map((n) => n.start)))].sort(
    (first, second) => first - second,
  );
  if (onsets.length < ENOUGH) return { file, warnings: [] };
  if (division >= FINE && exactShare(onsets, division) >= WRITTEN) return { file, warnings: [] };

  const notes = timedNotes(file);
  const attacks = attacksOf(notes);
  const heard = beatsOf(attacks);
  if (heard.length < 4) return { file, warnings: [] };
  const seconds = secondsOf(file);
  const own = offGrid(onsets.map((tick) => tick / division));
  const found = offGrid(attacks.map((attack) => beatClock(heard)(attack.time)));
  if (own < ADRIFT || found >= CLEARLY * own) return { file, warnings: [] };

  // The time a player took in a bar is not a beat of it: the bar keeps its length.
  const metre = metreOf(heard, attacks);
  const beats = heard.flatMap((beat, index) => (metre.taken.includes(index) ? [] : [beat]));
  const clock = beatClock(beats);
  const beatTicks = metre.compound ? (PPQ * 3) / 2 : PPQ;
  const barTicks = beatTicks * metre.beats;
  const raw = (time: number): number => (clock(time) - metre.phase) * beatTicks;
  const earliest = Math.min(...notes.map((note) => raw(note.start)));
  const shift = earliest < 0 ? Math.ceil(-earliest / barTicks) * barTicks : 0;
  const tick = (time: number): number => Math.max(0, Math.round(raw(time) + shift));

  const tracks: MidiTrack[] = file.tracks.map((track) => ({
    ...track,
    notes: track.notes.map((note) => {
      const start = tick(seconds(note.start));
      return { ...note, start, ticks: Math.max(1, tick(seconds(note.start + note.ticks)) - start) };
    }),
  }));
  const gaps = beats.slice(1).map((time, index) => time - (beats[index] as number));
  const beatSeconds = median(gaps);
  const microsPerQuarter = Math.round((beatSeconds * 1e6 * PPQ) / beatTicks);
  const time = metre.compound
    ? { tick: 0, beats: metre.beats * 3, beatType: 8 }
    : { tick: 0, beats: metre.beats, beatType: 4 };
  // A key stated before the first note opens the piece, pickup or not.
  const first = Math.min(...notes.map((note) => note.start));
  const stated = file.keys.map((mark) => {
    const at = seconds(mark.tick);
    return { ...mark, tick: at <= first ? 0 : tick(at) };
  });
  const guessed = stated.length === 0 ? keyOf(notes) : null;
  const keys = guessed === null ? stated : [{ tick: 0, ...guessed }];

  const bpm = Math.round(60 / beatSeconds);
  const warnings = [
    `this file was played rather than written, so its beats, its metre (${time.beats}/${time.beatType}) and its tempo (about ${bpm} beats a minute) are the editor's reading of the performance`,
  ];
  if (guessed !== null)
    warnings.push("it states no key, so the key it is spelled in was read from the notes");
  return {
    file: {
      ...file,
      division: PPQ,
      tracks,
      tempos: [{ tick: 0, microsPerQuarter }],
      times: [time],
      keys,
    },
    warnings,
  };
}
